import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useApi } from './ApiContext';
import { useUserContext } from './UserContext';

export interface DockChat {
  teamId: string;
  teamName: string;
}

/** User-dragged size of the small (non-maximized) dock window, in px */
export interface DockSize {
  width: number;
  height: number;
}

/** A one-shot request to open a specific conversation in a team's chat */
export interface RequestedConversation {
  teamId: string;
  conversationId: number;
  /** Distinguishes repeat requests for the same conversation */
  nonce: number;
}

/**
 * A one-shot "chat about this wiki selection" request: opens a fresh
 * conversation carrying the origin page (wiki_page channel) with the
 * composer pre-filled with the quoted selection
 */
export interface RequestedWikiChat {
  teamId: string;
  wikiScope: { projectId: number; pagePath: string };
  seedText: string;
  /** Distinguishes repeat requests for the same page */
  nonce: number;
}

/**
 * A one-shot "start a chat with this member" request: opens a fresh
 * conversation with the composer pre-filled with their @mention
 */
export interface RequestedMemberChat {
  teamId: string;
  /** Mention name exactly as the composer autocomplete knows the member */
  mentionName: string;
  /** Distinguishes repeat requests for the same member */
  nonce: number;
}

/**
 * A one-shot "start a chat with this agent" request. The composer stays
 * empty; the selected agent is carried as structured routing metadata.
 */
export interface RequestedAgentChat {
  teamId: string;
  agent: { id: number; name: string };
  /** Distinguishes repeat requests for the same agent */
  nonce: number;
}

interface ChatDockContextValue {
  /** One chat per team the user belongs to; empty while logged out */
  chats: DockChat[];
  activeTeamId: string | null;
  minimized: boolean;
  /** Floating window expanded to fill the viewport (still the dock, not the full-page chat) */
  maximized: boolean;
  /** Custom drag-resized size of the small window; null = default CSS size */
  dockSize: DockSize | null;
  /**
   * Team whose chat is embedded full-page (the team's Chat page); the
   * floating dock hides itself and releases that team's chat while set
   */
  embeddedTeamId: string | null;
  /** Pending "open this conversation" request, consumed by TeamChat */
  requestedConversation: RequestedConversation | null;
  /** Pending "chat with this member" request, consumed by TeamChat */
  requestedMemberChat: RequestedMemberChat | null;
  /** Pending "chat with this agent" request, consumed by TeamChat */
  requestedAgentChat: RequestedAgentChat | null;
  /** Pending "chat about this wiki selection" request, consumed by TeamChat */
  requestedWikiChat: RequestedWikiChat | null;
  setActive: (teamId: string) => void;
  setMinimized: (minimized: boolean) => void;
  setMaximized: (maximized: boolean) => void;
  setDockSize: (size: DockSize | null) => void;
  setEmbedded: (teamId: string | null) => void;
  /** Open (or focus) a team's chat on a specific conversation */
  openConversation: (teamId: string, conversationId: number) => void;
  consumeRequestedConversation: () => void;
  /** Open a team's chat on a new conversation addressed to a member */
  startMemberChat: (teamId: string, mentionName: string) => void;
  consumeRequestedMemberChat: () => void;
  /** Open a team's chat on a new conversation targeted to an agent */
  startAgentChat: (
    teamId: string,
    agent: { id: number; name: string },
  ) => void;
  consumeRequestedAgentChat: () => void;
  /** Open a team's chat on a new wiki-origin conversation with a seeded quote */
  startWikiChat: (
    teamId: string,
    wikiScope: { projectId: number; pagePath: string },
    seedText: string,
  ) => void;
  consumeRequestedWikiChat: () => void;
}

const STORAGE_KEY = 'chatDock.v4';

const ChatDockContext = createContext<ChatDockContextValue | undefined>(
  undefined,
);

function loadInitialState(): {
  activeTeamId: string | null;
  minimized: boolean;
  maximized: boolean;
  dockSize: DockSize | null;
} {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const width = Number(parsed.dockSize?.width);
      const height = Number(parsed.dockSize?.height);
      return {
        activeTeamId: parsed.activeTeamId ?? null,
        minimized: !!parsed.minimized,
        maximized: !!parsed.maximized,
        dockSize:
          Number.isFinite(width) && Number.isFinite(height)
            ? { width, height }
            : null,
      };
    }
  } catch {
    // Fresh state on any parse problem
  }
  // First login: show the always-available launcher without covering forms.
  return {
    activeTeamId: null,
    minimized: true,
    maximized: false,
    dockSize: null,
  };
}

/**
 * The always-available team chat dock: as soon as the user is logged in,
 * a chat exists for each of their teams (tabs switch between teams). Only
 * the minimized/active state is user-controlled — there is no closing.
 * Chats stay mounted in ChatDock so sockets and transcripts survive
 * navigation; minimized/active choices survive reloads (sessionStorage).
 */
export const ChatDockProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { teamApi } = useApi();
  const { isLoggedIn, user } = useUserContext();
  const location = useLocation();
  const [chats, setChats] = useState<DockChat[]>([]);
  const [state, setState] = useState(loadInitialState);
  const userChangedLayoutRef = useRef(false);
  const [embeddedTeamId, setEmbeddedTeamId] = useState<string | null>(null);
  const [requestedConversation, setRequestedConversation] =
    useState<RequestedConversation | null>(null);
  const [requestedMemberChat, setRequestedMemberChat] =
    useState<RequestedMemberChat | null>(null);
  const [requestedAgentChat, setRequestedAgentChat] =
    useState<RequestedAgentChat | null>(null);
  const [requestedWikiChat, setRequestedWikiChat] =
    useState<RequestedWikiChat | null>(null);

  useEffect(() => {
    if (!isLoggedIn) {
      setChats([]);
      return;
    }

    let cancelled = false;
    teamApi
      .apiTeamsGet({})
      .then((response) => {
        if (cancelled) return;
        const nextChats = (response.data || [])
          .filter((team) => team.id !== undefined && team.name)
          .map((team) => ({
            teamId: String(team.id),
            teamName: team.name!,
          }))
          .sort((a, b) =>
            a.teamName.toLowerCase().localeCompare(b.teamName.toLowerCase()),
          );

        setChats(nextChats);
        setState((current) => ({
          ...current,
          activeTeamId: nextChats.some(
            (chat) => chat.teamId === current.activeTeamId,
          )
            ? current.activeTeamId
            : (nextChats[0]?.teamId ?? null),
        }));
      })
      .catch((error) => {
        console.error('Chat dock: failed to load teams:', error);
        if (!cancelled) setChats([]);
      });
    return () => {
      cancelled = true;
    };
  }, [teamApi, isLoggedIn, location.pathname, user?.id]);

  useEffect(() => {
    if (!userChangedLayoutRef.current) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Storage full/unavailable: dock still works for this page
    }
  }, [state]);

  const setActive = useCallback((teamId: string) => {
    userChangedLayoutRef.current = true;
    setState((current) => ({
      ...current,
      activeTeamId: teamId,
      minimized: false,
    }));
  }, []);

  const setMinimized = useCallback((minimized: boolean) => {
    userChangedLayoutRef.current = true;
    // Maximized survives minimizing: reopening from the launcher returns
    // to whatever size the window had
    setState((current) => ({ ...current, minimized }));
  }, []);

  const setMaximized = useCallback((maximized: boolean) => {
    userChangedLayoutRef.current = true;
    setState((current) => ({ ...current, maximized }));
  }, []);

  const setDockSize = useCallback((dockSize: DockSize | null) => {
    userChangedLayoutRef.current = true;
    setState((current) => ({ ...current, dockSize }));
  }, []);

  const openConversation = useCallback(
    (teamId: string, conversationId: number) => {
      userChangedLayoutRef.current = true;
      setState((current) => ({
        ...current,
        activeTeamId: teamId,
        minimized: false,
      }));
      setRequestedConversation((current) => ({
        teamId,
        conversationId,
        nonce: (current?.nonce ?? 0) + 1,
      }));
    },
    [],
  );

  const consumeRequestedConversation = useCallback(() => {
    setRequestedConversation(null);
  }, []);

  const startMemberChat = useCallback((teamId: string, mentionName: string) => {
    userChangedLayoutRef.current = true;
    setState((current) => ({
      ...current,
      activeTeamId: teamId,
      minimized: false,
    }));
    setRequestedMemberChat((current) => ({
      teamId,
      mentionName,
      nonce: (current?.nonce ?? 0) + 1,
    }));
  }, []);

  const consumeRequestedMemberChat = useCallback(() => {
    setRequestedMemberChat(null);
  }, []);

  const startAgentChat = useCallback(
    (teamId: string, agent: { id: number; name: string }) => {
      userChangedLayoutRef.current = true;
      setState((current) => ({
        ...current,
        activeTeamId: teamId,
        minimized: false,
      }));
      setRequestedAgentChat((current) => ({
        teamId,
        agent,
        nonce: (current?.nonce ?? 0) + 1,
      }));
    },
    [],
  );

  const consumeRequestedAgentChat = useCallback(() => {
    setRequestedAgentChat(null);
  }, []);

  const startWikiChat = useCallback(
    (
      teamId: string,
      wikiScope: { projectId: number; pagePath: string },
      seedText: string,
    ) => {
      userChangedLayoutRef.current = true;
      setState((current) => ({
        ...current,
        activeTeamId: teamId,
        minimized: false,
      }));
      setRequestedWikiChat((current) => ({
        teamId,
        wikiScope,
        seedText,
        nonce: (current?.nonce ?? 0) + 1,
      }));
    },
    [],
  );

  const consumeRequestedWikiChat = useCallback(() => {
    setRequestedWikiChat(null);
  }, []);

  const setEmbedded = useCallback((teamId: string | null) => {
    setEmbeddedTeamId(teamId);
    // Leaving the full-page chat resumes the same team in the dock
    if (teamId) {
      setState((current) => ({ ...current, activeTeamId: teamId }));
    }
  }, []);

  return (
    <ChatDockContext.Provider
      value={{
        chats,
        ...state,
        embeddedTeamId,
        requestedConversation,
        requestedMemberChat,
        requestedAgentChat,
        requestedWikiChat,
        setActive,
        setMinimized,
        setMaximized,
        setDockSize,
        setEmbedded,
        openConversation,
        consumeRequestedConversation,
        startMemberChat,
        consumeRequestedMemberChat,
        startAgentChat,
        consumeRequestedAgentChat,
        startWikiChat,
        consumeRequestedWikiChat,
      }}>
      {children}
    </ChatDockContext.Provider>
  );
};

/**
 * Like useChatDock, but usable on surfaces that may render without the
 * provider (tests, embeds): returns null instead of throwing, so a
 * dock-dependent affordance can simply not render.
 */
export function useOptionalChatDock(): ChatDockContextValue | null {
  return useContext(ChatDockContext) ?? null;
}

export function useChatDock(): ChatDockContextValue {
  const context = useContext(ChatDockContext);
  if (!context) {
    throw new Error('useChatDock must be used within a ChatDockProvider');
  }
  return context;
}
